

import React, { useEffect, useState } from "react";

import CountUp from "react-countup";
import {
  PieChart, Pie, AreaChart, Area, LineChart, Line, Tooltip, Legend, Label,
  XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  BarChart, Bar,
  Cell,
} from "recharts";

import {
  CHART_PALETTE,
  CHART_COLORS,
  chartMargin,
  ChartGradient,
  LANGUAGE_CHART_COLORS,
  currencyTooltipFormatter,
  defaultLegend,
  defaultXAxis,
  defaultYAxis,
  defaultTooltip,
  lineTooltip,
  barRadius,
  withZeroBaseline,
  getDotProps,
  getActiveDotProps,
} from "./chartTheme";
import ChartPeriodTabs from "./ChartPeriodTabs";

import "./HomeStyle.css";
import { FiUsers, FiCalendar, FiStar } from "react-icons/fi";
import { FaAndroid } from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import "react-datepicker/dist/react-datepicker.css";
import {
  getDashboard,
  getDashboardTemp,
  getEventGraph,
  getPrice,
  getRevenueGraph,
  getUserGraph,
  getHostAndGuestGraph,
  getActiveHostAndGuestGraph,
  getRsvpsAndMessagesGraph,
  getPhotoSharingGraph,
  getTasksGraph,
  getSubscriptionGraph,
  getInvitesGraph
} from "../../../services/UserServices/UserService";
import PageTitle from "../../layouts/PageTitle";

const Home = (props) => {


  const [userInfo, setUserInfo] = useState({})
  const [userGraph, setUserGraph] = useState([])
  const [eventGraph, setEventrGraph] = useState([])
  const [revenueGraph, setRevenueGraph] = useState([])
  const [viewType, setViewType] = useState("monthly");
  const [hostGuestFilterType, setHostGuestFilterType] = useState("monthly");
  const [hostAndGuestData, setHostAndGuestData] = useState([]);
  const [hostGuestTotals, setHostGuestTotals] = useState({ totalHosts: 0, totalGuests: 0 });
  const [activeHostGuestFilterType, setActiveHostGuestFilterType] = useState("monthly");
  const [activeHostAndGuestData, setActiveHostAndGuestData] = useState([]);
  const [activeHostGuestTotals, setActiveHostGuestTotals] = useState({ totalHosts: 0, totalGuests: 0 });
  const [rsvpMessagesFilterType, setRsvpMessagesFilterType] = useState("monthly");
  const [rsvpMessagesData, setRsvpMessagesData] = useState([]);
  const [rsvpMessagesTotals, setRsvpMessagesTotals] = useState({ totalRsvp: 0, totalMessages: 0 });
  const [invitesFilterType, setInvitesFilterType] = useState("monthly");
  const [invitesData, setInvitesData] = useState([]);
  const [invitesTotals, setInvitesTotals] = useState({ totalInvites: 0 });
  const [photoSharingFilterType, setPhotoSharingFilterType] = useState("yearly");
  const [photoSharingData, setPhotoSharingData] = useState([]);
  const [photoSharingTotals, setPhotoSharingTotals] = useState({ totalPhotos: 0 });
  const [tasksFilterType, setTasksFilterType] = useState("monthly");
  const [tasksData, setTasksData] = useState([]);
  const [tasksTotals, setTasksTotals] = useState({ totalTasks: 0 });
  const [subscriptionRevenueData, setSubscriptionRevenueData] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const fetchUserInfo = async () => {
    try {
      const res = await getDashboard();
      setUserInfo(res?.data?.data)
    } catch (error) {
      console.error(error)
    }
  }
  const fetchUserGraph = async () => {
    try {
      const res = await getUserGraph();
      setUserGraph(res?.data?.data)
    } catch (error) {
      console.error(error)
    }
  }
  const fetchEventGraph = async () => {
    try {
      const res = await getEventGraph();
      setEventrGraph(res?.data?.data)
    } catch (error) {
      console.error(error)
    }
  }
  const chartKeys = React.useMemo(() => {
    if (!eventGraph?.length) return [];

    const keys = new Set();

    eventGraph.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== "month") {
          keys.add(key);
        }
      });
    });

    return Array.from(keys);
  }, [eventGraph]);
  const fetchRevenueGraph = async () => {
    try {
      const res = await getRevenueGraph();
      setRevenueGraph(res?.data?.data?.map(item => ({
        name: `${item.name} (${item.value})`,  // <-- label inside name
        value: item.value,
        revenue: item.revenue
      })) || [])
    } catch (error) {
      console.error(error)
    }
  }
  const formatGraphData = (dataArray) => {
    if (!Array.isArray(dataArray)) return [];
    return dataArray.map(item => {
      const labelKey = Object.keys(item).find(k => ['month', 'label', 'period', 'date', '_id', 'day', 'name'].includes(k));
      const hostsKey = Object.keys(item).find(k => k.toLowerCase().includes('host'));
      const guestsKey = Object.keys(item).find(k => k.toLowerCase().includes('guest'));

      return {
        ...item,
        label: item[labelKey] || "",
        period: item[labelKey] || "",
        hosts: item[hostsKey] || 0,
        guests: item[guestsKey] || 0,
      };
    });
  };

  const fetchHostAndGuestGraph = async () => {
    try {
      const res = await getHostAndGuestGraph(hostGuestFilterType);
      const payload = res?.data?.data;
      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        setHostAndGuestData(formatGraphData(payload.data));
        setHostGuestTotals({
          totalHosts: payload.totalHosts || 0,
          totalGuests: payload.totalGuests || 0
        });
      } else {
        setHostAndGuestData(formatGraphData(payload));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchActiveHostAndGuestGraph = async () => {
    try {
      const res = await getActiveHostAndGuestGraph(activeHostGuestFilterType);
      const payload = res?.data?.data;
      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        setActiveHostAndGuestData(formatGraphData(payload.data));
        setActiveHostGuestTotals({
          totalHosts: payload.totalHosts || 0,
          totalGuests: payload.totalGuests || 0
        });
      } else {
        setActiveHostAndGuestData(formatGraphData(payload));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const formatRsvpGraphData = (dataArray) => {
    if (!Array.isArray(dataArray)) return [];
    return dataArray.map(item => {
      const labelKey = Object.keys(item).find(k => ['month', 'label', 'period', 'date', '_id', 'day', 'name'].includes(k));
      return {
        ...item,
        period: item[labelKey] || "",
        rsvp: item.rsvp || 0,
        hostRSVPs: item.hostRSVPs || item.hostRsvp || item.hostRsvps || 0,
        guestRSVPs: item.guestRSVPs || item.guestRsvp || item.guestRsvps || 0,
        hostMessages: item.hostMessages || item.hostMessage || 0,
        guestMessages: item.guestMessages || item.guestMessage || 0,
      };
    });
  };

  const fetchRsvpMessagesGraph = async () => {
    try {
      const res = await getRsvpsAndMessagesGraph(rsvpMessagesFilterType);
      const payload = res?.data?.data;
      let targetData = payload;
      let totalRsvp = 0;
      let totalMessages = 0;

      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        targetData = payload.data;
        totalRsvp = payload.totalRsvp || payload.totalRsvps || 0;
        totalMessages = payload.totalMessages || payload.totalMessage || 0;
      }

      const formatted = formatRsvpGraphData(targetData);

      if (Array.isArray(payload)) {
        totalRsvp = formatted.reduce((s, i) => s + (i.rsvp || 0), 0);
        totalMessages = formatted.reduce((s, i) => s + (i.hostMessages || 0) + (i.guestMessages || 0), 0);
      }

      setRsvpMessagesTotals({ totalRsvp, totalMessages });
      setRsvpMessagesData(formatted);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchUserInfo()
    fetchUserGraph()
    fetchEventGraph()
    fetchRevenueGraph()
  }, [])

  useEffect(() => {
    fetchHostAndGuestGraph();
  }, [hostGuestFilterType])

  useEffect(() => {
    fetchActiveHostAndGuestGraph();
  }, [activeHostGuestFilterType])

  useEffect(() => {
    fetchRsvpMessagesGraph();
  }, [rsvpMessagesFilterType])

  const fetchInvitesGraph = async () => {
    try {
      const res = await getInvitesGraph(invitesFilterType);
      const payload = res?.data?.data;
      let targetData = payload;
      let totalInvites = 0;

      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        targetData = payload.data;
        totalInvites = payload.totalInvites || payload.totalInvite || 0;
      }

      const formatted = (targetData || []).map(item => {
        const labelKey = Object.keys(item).find(k => ['month', 'label', 'period', 'date', '_id', 'day', 'name'].includes(k));
        return {
          ...item,
          period: item[labelKey] || "",
          invites: item.invites || item.totalInvites || item.count || 0
        };
      });

      if (Array.isArray(payload)) {
        totalInvites = formatted.reduce((s, i) => s + (i.invites || 0), 0);
      }

      setInvitesTotals({ totalInvites });
      setInvitesData(formatted);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchInvitesGraph();
  }, [invitesFilterType]);


  const fetchPhotoSharingGraph = async () => {
    try {
      const res = await getPhotoSharingGraph(photoSharingFilterType);
      const payload = res?.data?.data;
      let targetData = payload;
      let totalPhotos = 0;

      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        targetData = payload.data;
        totalPhotos = payload.totalPhotos || payload.totalPhoto || 0;
      }

      const formatted = (targetData || []).map(item => {
        const labelKey = Object.keys(item).find(k => ['month', 'label', 'period', 'date', '_id', 'day', 'name'].includes(k));
        const hostKey = Object.keys(item).find(k => k.toLowerCase().includes('host'));
        const guestKey = Object.keys(item).find(k => k.toLowerCase().includes('guest'));
        return {
          ...item,
          period: item[labelKey] || "",
          host: item[hostKey] || 0,
          guest: item[guestKey] || 0
        };
      });

      if (Array.isArray(payload)) {
        totalPhotos = formatted.reduce((s, i) => s + (i.host || 0) + (i.guest || 0), 0);
      }

      setPhotoSharingTotals({ totalPhotos });
      setPhotoSharingData(formatted);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPhotoSharingGraph();
  }, [photoSharingFilterType]);


  const fetchTasksGraph = async () => {
    try {
      const res = await getTasksGraph(tasksFilterType);
      const payload = res?.data?.data;
      let targetData = payload;
      let totalTasks = 0;

      if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        targetData = payload.data;
        totalTasks = payload.totalTasks || payload.totalTask || 0;
      }

      const formatted = (targetData || []).map(item => {
        const labelKey = Object.keys(item).find(k => ['month', 'label', 'period', 'date', '_id', 'day', 'name'].includes(k));
        return {
          ...item,
          period: item[labelKey] || "",
          total: item.total || 0,
          host: item.host || 0,
          guest: item.guest || 0
        };
      });

      if (Array.isArray(payload)) {
        totalTasks = formatted.reduce((s, i) => s + (i.total || 0), 0);
      }

      setTasksTotals({ totalTasks });
      setTasksData(formatted);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchTasksGraph();
  }, [tasksFilterType]);





  const templateUsageData = userInfo?.mostPopularTemplate || [];
  const fetchSubscriptionGraph = async () => {
    try {
      const res = await getSubscriptionGraph();
      const formatted = (res?.data?.data || []).map(item => {
        const labelKey = Object.keys(item).find(k => ['month', 'label', 'period', 'date', '_id', 'day', 'name'].includes(k));
        return {
          ...item,
          period: item[labelKey] || "",
          elite: item.elite || item.Elite || 0,
          premium: item.premium || item.Premium || 0,
          essential: item.essential || item.Essential || 0,
          planOne: item.planOne || item['Plan 1'] || item.PlanOne || 0,
        };
      });
      setSubscriptionRevenueData(formatted);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchSubscriptionGraph();
  }, []);

  const totalRevenue = subscriptionRevenueData.reduce(
    (sum, item) => sum + (item.revenue || 0),
    0
  );






  const exportAnalyticsCSV = () => {
    let csv = "";

    // ---------------- PIE CHART DATA ----------------
    csv += "User Insights (Pie Chart)\n";
    csv += "Name,Value\n";
    revenueGraph.forEach((item) => {
      csv += `${item.name},${item.value}\n`;
    });

    csv += "\n";

    // ---------------- LINE CHART DATA ----------------
    csv += "Engagement Analytics (Line Chart)\n";
    csv += "Day,User Count\n";
    userGraph.forEach((item) => {
      csv += `${item.day},${item.userCount}\n`;
    });

    csv += "\n";

    // ---------------- HEATMAP DATA ----------------
    csv += "Event Heatmap\n";
    const dynamicKeys = [
      ...new Set(
        eventGraph.flatMap(item => Object.keys(item).filter(k => k !== "month"))
      )
    ];
    csv += `Month,${dynamicKeys.join(",")}\n`;

    eventGraph.forEach(item => {
      const row = [item.month, ...dynamicKeys.map(key => item[key] ?? "")];
      csv += row.join(",") + "\n";
    });

    // ---------------- DOWNLOAD FILE ----------------
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `analytics_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToExcel = (data, fileName, sheetName = "Sheet1") => {
    if (!data || !data.length) return;

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(blob, `${fileName}.xlsx`);
  };


  const hostGuestMonthlyData = [{ month: "Jan", hosts: 40, guests: 120 }, { month: "Feb", hosts: 55, guests: 150 }, { month: "Mar", hosts: 70, guests: 180 }, { month: "Apr", hosts: 65, guests: 170 }, { month: "May", hosts: 90, guests: 210 }, { month: "June", hosts: 20, guests: 110 }, { month: "July", hosts: 50, guests: 20 }, { month: "Aug", hosts: 50, guests: 20 }, { month: "Sep", hosts: 50, guests: 22 }, { month: "Nov", hosts: 5, guests: 20 }, { month: "Oct", hosts: 10, guests: 90 }, { month: "Dec", hosts: 5, guests: 30 },];
  const hostGuestRawData = [
    { date: "2024-01-02", hosts: 5, guests: 20 },
    { date: "2024-01-10", hosts: 10, guests: 30 },
    { date: "2024-02-05", hosts: 25, guests: 60 },
    { date: "2024-02-20", hosts: 30, guests: 50 },
    { date: "2024-03-12", hosts: 40, guests: 70 },
    { date: "2024-03-28", hosts: 20, guests: 40 },
  ];


  const userInsights = [
    { name: "Active Users", value: 660 },
    { name: "Retention", value: 74 },
    { name: "Conversion", value: 42 },
  ]
  const COLORS = CHART_PALETTE;

  const hostGuestChartData = React.useMemo(
    () => withZeroBaseline(hostAndGuestData, "label", ["hosts", "guests"]),
    [hostAndGuestData]
  );

  const activeHostGuestChartData = React.useMemo(
    () => withZeroBaseline(activeHostAndGuestData, "period", ["hosts", "guests"]),
    [activeHostAndGuestData]
  );

  const invitesChartData = React.useMemo(
    () => withZeroBaseline(invitesData, "period", ["invites"]),
    [invitesData]
  );

  const activeUsersData = [
    { period: "Jan", hosts: 120, guests: 340 },
    { period: "Feb", hosts: 150, guests: 380 },
    { period: "Mar", hosts: 180, guests: 420 },
    { period: "Apr", hosts: 210, guests: 460 },
    { period: "May", hosts: 260, guests: 520 },
  ];
  const activeUsersRawData = [
    { date: "2024-01-05", hosts: 120, guests: 340 },
    { date: "2024-01-20", hosts: 150, guests: 380 },
    { date: "2024-02-10", hosts: 180, guests: 420 },
    { date: "2024-02-25", hosts: 210, guests: 460 },
    { date: "2024-03-15", hosts: 260, guests: 520 },
  ];
  const getActiveUsersChartData = () => {
    let data = activeUsersRawData;

    if (startDate && endDate) {
      data = data.filter(item => {
        const d = new Date(item.date);
        return d >= new Date(startDate) && d <= new Date(endDate);
      });
    }

    const grouped = {};

    data.forEach(item => {
      const d = new Date(item.date);
      let key = "";

      if (viewType === "daily") {
        key = d.toLocaleDateString();
      }

      if (viewType === "weekly") {
        const week = Math.ceil(d.getDate() / 7);
        key = `Week ${week} ${d.toLocaleString("default", { month: "short" })}`;
      }

      if (viewType === "monthly") {
        key = d.toLocaleString("default", { month: "short" });
      }

      if (!grouped[key]) {
        grouped[key] = { period: key, hosts: 0, guests: 0 };
      }

      grouped[key].hosts += item.hosts;
      grouped[key].guests += item.guests;
    });

    return Object.values(grouped);
  };

  const activeUsersDataM = getActiveUsersChartData();


  const getChartData = (newData) => {
    let data = newData;

    if (startDate && endDate) {
      data = data.filter(item => {
        const d = new Date(item.date);
        return d >= new Date(startDate) && d <= new Date(endDate);
      });
    }

    const grouped = {};

    data.forEach(item => {
      const d = new Date(item.date);
      let key = "";

      if (viewType === "daily") {
        key = d.toLocaleDateString();
      }

      if (viewType === "weekly") {
        const week = Math.ceil(d.getDate() / 7);
        key = `Week ${week} ${d.toLocaleString("default", { month: "short" })}`;
      }

      if (viewType === "monthly") {
        key = d.toLocaleString("default", { month: "short" });
      }

      if (!grouped[key]) {
        grouped[key] = { label: key, hosts: 0, guests: 0 };
      }

      grouped[key].hosts += item.hosts;
      grouped[key].guests += item.guests;
    });

    return Object.values(grouped);
  };




  const rsvpMessageRawData = [
    {
      date: "2024-01-05",
      hostRSVPs: 40,
      guestRSVPs: 120,
      hostMessages: 90,
      guestMessages: 220,
    },
    {
      date: "2024-02-10",
      hostRSVPs: 55,
      guestRSVPs: 150,
      hostMessages: 110,
      guestMessages: 260,
    },
    {
      date: "2024-03-18",
      hostRSVPs: 70,
      guestRSVPs: 180,
      hostMessages: 150,
      guestMessages: 310,
    },
  ];
  const getRSVPMessageChartData = () => {
    let data = rsvpMessageRawData;

    if (startDate && endDate) {
      data = data.filter(item => {
        const d = new Date(item.date);
        return d >= new Date(startDate) && d <= new Date(endDate);
      });
    }

    const grouped = {};

    data.forEach(item => {
      const d = new Date(item.date);
      let key = "";

      if (viewType === "daily") {
        key = d.toLocaleDateString();
      }

      if (viewType === "weekly") {
        const week = Math.ceil(d.getDate() / 7);
        key = `Week ${week} ${d.toLocaleString("default", { month: "short" })}`;
      }

      if (viewType === "monthly") {
        key = d.toLocaleString("default", { month: "short" });
      }

      if (!grouped[key]) {
        grouped[key] = {
          period: key,
          hostRSVPs: 0,
          guestRSVPs: 0,
          hostMessages: 0,
          guestMessages: 0,
        };
      }

      grouped[key].hostRSVPs += item.hostRSVPs;
      grouped[key].guestRSVPs += item.guestRSVPs;
      grouped[key].hostMessages += item.hostMessages;
      grouped[key].guestMessages += item.guestMessages;
    });

    return Object.values(grouped);
  };
  const rsvpMessageData = getRSVPMessageChartData();



  const languageUsageData = userInfo?.languages
    ? Object.entries(userInfo.languages).map(([name, value]) => ({ name, value }))
    : [];

  const languageLegendData = React.useMemo(() => {
    const total = languageUsageData.reduce((sum, item) => sum + item.value, 0);
    return [...languageUsageData]
      .sort((a, b) => b.value - a.value)
      .map((item) => ({
        ...item,
        pct: total ? Math.round((item.value / total) * 100) : 0,
      }));
  }, [languageUsageData]);

  const languageChartData = React.useMemo(
    () => languageLegendData.filter((item) => item.value > 0),
    [languageLegendData]
  );

  const languageChartTotal = languageChartData.reduce((sum, item) => sum + item.value, 0);
  const topLanguage = languageChartData[0];

  const getLanguageColor = (item, index) => {
    if (!item.value) return "#E4E7EC";
    return LANGUAGE_CHART_COLORS[index % LANGUAGE_CHART_COLORS.length];
  };





  const mmsInvitesData = [
    { period: "Jan", invites: 320 },
    { period: "Feb", invites: 410 },
    { period: "Mar", invites: 520 },
    { period: "Apr", invites: 610 },
    { period: "May", invites: 740 },
  ];

  const planEarningsData = [
    { plan: "Basic", amount: 45000 },
    { plan: "Standard", amount: 78000 },
    { plan: "Premium", amount: 120000 },
  ];

  return (
    <>
      <PageTitle pageTitle="Dashboard" subtitle="Overview of platform activity and metrics" />

      <div className="qx-space">
      <div className="row g-4 mb-0 row-cols-1 row-cols-sm-2 row-cols-xl-4">
        <div className="col">
          <div className="qx-metric-card">
            <div className="qx-metric-icon">
              <FiUsers size={24} />
            </div>
            <div className="qx-metric-body">
              <span className="qx-metric-label">Active Hosts</span>
              <h4 className="qx-metric-value">{userInfo?.activeHosts || 0}</h4>
              <p className="qx-metric-sub">Hosts currently active</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="qx-metric-card">
            <div className="qx-metric-icon">
              <FiCalendar size={24} />
            </div>
            <div className="qx-metric-body">
              <span className="qx-metric-label">Total Events</span>
              <h4 className="qx-metric-value">{userInfo?.totalEvents || 0}</h4>
              <p className="qx-metric-sub">All events created</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="qx-metric-card">
            <div className="qx-metric-icon">
              <FiStar size={24} />
            </div>
            <div className="qx-metric-body">
              <span className="qx-metric-label">Top Template</span>
              <h4 className="qx-metric-value" style={{ fontSize: "20px", lineHeight: "28px" }} title={userInfo?.TopPerformingTemplates?.name}>
                {userInfo?.TopPerformingTemplates?.name || "N/A"}
              </h4>
              <p className="qx-metric-sub">Best performing template</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="qx-metric-card">
            <div className="qx-metric-icon">
              <FaAndroid size={24} />
            </div>
            <div className="qx-metric-body">
              <span className="qx-metric-label">Android Devices</span>
              <h4 className="qx-metric-value">{userInfo?.androidCount || 0}</h4>
              <p className="qx-metric-sub">iOS: {userInfo?.iosCount || 0}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 mt-0 qx-dashboard-charts">
          <div className="col-12 col-lg-6">
            <div className="premium-card p-4 qx-chart-card h-100">
              <div className="qx-chart-card-header">
                <div className="qx-chart-card-title d-flex flex-column align-items-start">
                  <h4 className="chart-title mb-2">Hosts & Guests</h4>
                  <div className="qx-chart-card-filters">
                    <ChartPeriodTabs value={hostGuestFilterType} onChange={setHostGuestFilterType} />
                  </div>
                </div>
                <div className="qx-chart-card-stats ms-auto">
                  <div className="stat-box">
                    <div className="stat-label">Total Hosts</div>
                    <div className="stat-value">
                      <CountUp start={0} end={hostGuestTotals.totalHosts} duration={3} />
                    </div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">Total Guests</div>
                    <div className="stat-value">
                      <CountUp start={0} end={hostGuestTotals.totalGuests} duration={3} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="qx-chart-wrap">
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={hostGuestChartData} margin={chartMargin}>
                  <CartesianGrid vertical={false} stroke="#E4E7EC" strokeDasharray="4 4" />
                  <XAxis {...defaultXAxis("label")} />
                  <YAxis {...defaultYAxis()} />
                  <Tooltip {...lineTooltip()} />
                  <Legend {...defaultLegend} />
                  <Line
                    type="monotone"
                    dataKey="hosts"
                    name="Hosts"
                    stroke={CHART_COLORS.primary}
                    strokeWidth={3}
                    dot={getDotProps(hostAndGuestData.length, CHART_COLORS.primary)}
                    activeDot={getActiveDotProps(CHART_COLORS.primary)}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="guests"
                    name="Guests"
                    stroke={CHART_COLORS.secondary}
                    strokeWidth={3}
                    dot={getDotProps(hostAndGuestData.length, CHART_COLORS.secondary)}
                    activeDot={getActiveDotProps(CHART_COLORS.secondary)}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
              </div>
              <div className="d-flex justify-content-between">
                <div></div>
                <button
                  className="download-btn"
                  onClick={() => {
                    const totalHosts = userInfo?.activeHosts || 0;
                    const totalGuests = userInfo?.activeGuests || 0;
                    const filteredData = hostAndGuestData;

                    const excelData = [
                      {
                        label: "Total",
                        hosts: filteredData.reduce((s, i) => s + i.hosts, 0),
                        guests: filteredData.reduce((s, i) => s + i.guests, 0),
                      },
                      ...filteredData,
                    ]
                    exportToExcel(excelData, "hosts_guests_report");
                  }}

                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg> Export CSV
                </button>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="premium-card p-4 qx-chart-card h-100">
              <div className="qx-chart-card-header">
                <div className="qx-chart-card-title d-flex flex-column align-items-start">
                  <h4 className="chart-title mb-2">Active Hosts & Guests</h4>
                  <div className="qx-chart-card-filters">
                    <ChartPeriodTabs value={activeHostGuestFilterType} onChange={setActiveHostGuestFilterType} />
                  </div>
                </div>
                <div className="qx-chart-card-stats ms-auto">
                  <div className="stat-box">
                    <div className="stat-label">Total Hosts</div>
                    <div className="stat-value">
                      <CountUp start={0} end={activeHostGuestTotals.totalHosts} duration={3} />
                    </div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">Total Guests</div>
                    <div className="stat-value">
                      <CountUp start={0} end={activeHostGuestTotals.totalGuests} duration={3} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="qx-chart-wrap">
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={activeHostGuestChartData} margin={chartMargin}>
                    <CartesianGrid vertical={false} stroke="#E4E7EC" strokeDasharray="4 4" />
                    <XAxis {...defaultXAxis("period")} />
                    <YAxis {...defaultYAxis()} />
                    <Tooltip {...lineTooltip()} />
                    <Legend {...defaultLegend} />
                    <Line
                      type="monotone"
                      dataKey="hosts"
                      name="Active Hosts"
                      stroke={CHART_COLORS.primary}
                      strokeWidth={3}
                      dot={getDotProps(activeHostAndGuestData.length, CHART_COLORS.primary)}
                      activeDot={getActiveDotProps(CHART_COLORS.primary)}
                      connectNulls
                    />
                    <Line
                      type="monotone"
                      dataKey="guests"
                      name="Active Guests"
                      stroke={CHART_COLORS.emerald}
                      strokeWidth={3}
                      dot={getDotProps(activeHostAndGuestData.length, CHART_COLORS.emerald)}
                      activeDot={getActiveDotProps(CHART_COLORS.emerald)}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="d-flex justify-content-between">
                <div></div>
                <button
                  className="download-btn"
                  onClick={() => {
                    const excelData = [
                      {
                        period: "Total",
                        hosts: activeHostAndGuestData.reduce((s, i) => s + (i.hosts || 0), 0),
                        guests: activeHostAndGuestData.reduce((s, i) => s + (i.guests || 0), 0),
                      },
                      ...activeHostAndGuestData, // Then monthly breakdown
                    ];

                    // 3️⃣ Export
                    exportToExcel(excelData, "active_hosts_guests_report");
                  }}

                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg> Export CSV
                </button>
              </div>

            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="premium-card p-4 qx-chart-card h-100">
              <div className="qx-chart-card-header">
                <div className="qx-chart-card-title">
                  <h4 className="chart-title mb-0">Events</h4>
                  <p className="qx-card-desc mb-0">Event distribution by month and category</p>
                </div>
                <div className="qx-chart-card-filters" aria-hidden="true" />
                <div className="qx-chart-card-stats ms-auto">
                  <div className="stat-box">
                    <div className="stat-label">Total Events</div>
                    <div className="stat-value">
                      <CountUp start={0} end={userInfo?.totalEvents} duration={5} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="qx-chart-wrap">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart
                  data={eventGraph}
                  margin={chartMargin}
                  barCategoryGap="18%"
                >
                  <CartesianGrid vertical={false} stroke="#E4E7EC" strokeDasharray="4 4" />
                  <XAxis {...defaultXAxis("month")} />
                  <YAxis {...defaultYAxis()} />
                  <Tooltip {...defaultTooltip()} />
                  <Legend {...defaultLegend} />

                  {chartKeys.map((key, index) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      stackId="events"
                      fill={COLORS[index % COLORS.length]}
                      maxBarSize={44}
                      radius={index === chartKeys.length - 1 ? barRadius : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="premium-card p-4 qx-chart-card h-100">
              <div className="qx-chart-card-header">
                <div className="qx-chart-card-title d-flex flex-column align-items-start">
                  <h4 className="chart-title mb-2">RSVP & Messages</h4>
                  <div className="qx-chart-card-filters">
                    <ChartPeriodTabs value={rsvpMessagesFilterType} onChange={setRsvpMessagesFilterType} />
                  </div>
                </div>
                <div className="qx-chart-card-stats ms-auto">
                  <div className="stat-box">
                    <div className="stat-label">Total RSVP</div>
                    <div className="stat-value">
                      <CountUp start={0} end={rsvpMessagesTotals.totalRsvp} duration={3} />
                    </div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">Total Messages</div>
                    <div className="stat-value">
                      <CountUp start={0} end={rsvpMessagesTotals.totalMessages} duration={3} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="qx-chart-wrap">
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={rsvpMessagesData} margin={chartMargin} barCategoryGap="18%" barGap={4}>
                    <CartesianGrid vertical={false} stroke="#E4E7EC" strokeDasharray="4 4" />
                    <XAxis {...defaultXAxis("period")} />
                    <YAxis {...defaultYAxis()} />
                    <Tooltip {...defaultTooltip()} />
                    <Legend {...defaultLegend} />
                    <Bar dataKey="rsvp" name="RSVPs" fill={CHART_COLORS.accent} maxBarSize={28} radius={barRadius} />
                    <Bar dataKey="hostMessages" name="Host Messages" fill={CHART_COLORS.primary} maxBarSize={28} radius={barRadius} />
                    <Bar dataKey="guestMessages" name="Guest Messages" fill={CHART_COLORS.secondary} maxBarSize={28} radius={barRadius} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="d-flex justify-content-end mt-3">
                <button
                  className="download-btn"
                  onClick={() => {
                    const excelData = [
                      {
                        period: "Total",
                        rsvp: rsvpMessagesData.reduce((s, i) => s + (i.rsvp || 0), 0),
                        hostMessages: rsvpMessagesData.reduce((s, i) => s + (i.hostMessages || 0), 0),
                        guestMessages: rsvpMessagesData.reduce((s, i) => s + (i.guestMessages || 0), 0),
                      },
                      ...rsvpMessagesData,
                    ];
                    exportToExcel(excelData, "rsvp_messages_report");
                  }}
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg> Export CSV
                </button>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="premium-card p-4 qx-chart-card h-100">
              <div className="qx-chart-card-header">
                <div className="qx-chart-card-title d-flex flex-column align-items-start">
                  <h4 className="chart-title mb-2">Invites Sent</h4>
                  <div className="qx-chart-card-filters">
                    <ChartPeriodTabs value={invitesFilterType} onChange={setInvitesFilterType} />
                  </div>
                </div>
                <div className="qx-chart-card-stats ms-auto">
                  <div className="stat-box">
                    <div className="stat-label">Total Invites</div>
                    <div className="stat-value">
                      <CountUp start={0} end={invitesTotals.totalInvites} duration={5} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="qx-chart-wrap">
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={invitesChartData} margin={chartMargin}>
                  <ChartGradient id="invitesGradient" color={CHART_COLORS.indigo} />
                  <CartesianGrid vertical={false} stroke="#E4E7EC" strokeDasharray="4 4" />
                  <XAxis {...defaultXAxis("period")} />
                  <YAxis {...defaultYAxis()} />
                  <Tooltip {...lineTooltip()} />
                  <Legend {...defaultLegend} />
                  <Area
                    type="monotone"
                    dataKey="invites"
                    name="Invites Sent"
                    stroke={CHART_COLORS.indigo}
                    strokeWidth={3}
                    fill="url(#invitesGradient)"
                    dot={getDotProps(invitesData.length, CHART_COLORS.indigo)}
                    activeDot={getActiveDotProps(CHART_COLORS.indigo)}
                    connectNulls
                  />
                </AreaChart>
              </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="premium-card p-4 qx-chart-card h-100">
              <div className="qx-chart-card-header">
                <div className="qx-chart-card-title d-flex flex-column align-items-start">
                  <h4 className="chart-title mb-2">Photos Shared</h4>
                  <div className="qx-chart-card-filters">
                    <ChartPeriodTabs value={photoSharingFilterType} onChange={setPhotoSharingFilterType} />
                  </div>
                </div>
                <div className="qx-chart-card-stats ms-auto">
                  <div className="stat-box">
                    <div className="stat-label">Total Photos</div>
                    <div className="stat-value">
                      <CountUp start={0} end={photoSharingTotals.totalPhotos} duration={5} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="qx-chart-wrap">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={photoSharingData} margin={chartMargin} barCategoryGap="22%">
                  <CartesianGrid vertical={false} stroke="#E4E7EC" strokeDasharray="4 4" />
                  <XAxis {...defaultXAxis("period")} />
                  <YAxis {...defaultYAxis()} />
                  <Tooltip {...defaultTooltip()} />
                  <Legend {...defaultLegend} />

                  <Bar
                    dataKey="host"
                    name="Host Uploads"
                    fill={CHART_COLORS.primary}
                    maxBarSize={40}
                    radius={barRadius}
                  />

                  <Bar
                    dataKey="guest"
                    name="Guest Uploads"
                    fill={CHART_COLORS.accent}
                    maxBarSize={40}
                    radius={barRadius}
                  />
                </BarChart>
              </ResponsiveContainer>
              </div>
              <div className="d-flex justify-content-end mt-3">
                <button
                  className="download-btn"
                  onClick={() => {
                    const excelData = [
                      {
                        period: "Total",
                        host: photoSharingData.reduce((s, i) => s + (i.host || 0), 0),
                        guest: photoSharingData.reduce((s, i) => s + (i.guest || 0), 0),
                      },
                      ...photoSharingData,
                    ];
                    exportToExcel(excelData, "photos_shared_report");
                  }}
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg> Export CSV
                </button>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="premium-card p-4 qx-chart-card h-100">
              <div className="qx-chart-card-header">
                <div className="qx-chart-card-title d-flex flex-column align-items-start">
                  <h4 className="chart-title mb-2">Tasks</h4>
                  <div className="qx-chart-card-filters">
                    <ChartPeriodTabs value={tasksFilterType} onChange={setTasksFilterType} />
                  </div>
                </div>
                <div className="qx-chart-card-stats ms-auto">
                  <div className="stat-box">
                    <div className="stat-label">Total Tasks</div>
                    <div className="stat-value">
                      <CountUp start={0} end={tasksTotals.totalTasks} duration={5} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="qx-chart-wrap">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={tasksData} margin={chartMargin} barCategoryGap="18%" barGap={4}>
                  <CartesianGrid vertical={false} stroke="#E4E7EC" strokeDasharray="4 4" />
                  <XAxis {...defaultXAxis("period")} />
                  <YAxis {...defaultYAxis()} />
                  <Tooltip {...defaultTooltip()} />
                  <Legend {...defaultLegend} />

                  <Bar
                    dataKey="total"
                    name="Total Tasks"
                    fill={CHART_COLORS.accent}
                    maxBarSize={32}
                    radius={barRadius}
                  />

                  <Bar
                    dataKey="host"
                    name="Host Tasks"
                    fill={CHART_COLORS.primary}
                    maxBarSize={32}
                    radius={barRadius}
                  />

                  <Bar
                    dataKey="guest"
                    name="Guest Tasks"
                    fill={CHART_COLORS.secondary}
                    maxBarSize={32}
                    radius={barRadius}
                  />
                </BarChart>
              </ResponsiveContainer>
              </div>
              <div className="d-flex justify-content-end mt-3">
                <button
                  className="download-btn"
                  onClick={() => {
                    const excelData = [
                      {
                        period: "Total",
                        total: tasksData.reduce((s, i) => s + i.total, 0),
                        host: tasksData.reduce((s, i) => s + i.host, 0),
                        guest: tasksData.reduce((s, i) => s + i.guest, 0),
                      },
                      ...tasksData,
                    ];
                    exportToExcel(excelData, "tasks_activity_report");
                  }}
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg> Export CSV
                </button>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="premium-card p-4 qx-chart-card h-100">
              <div className="qx-chart-card-header">
                <div className="qx-chart-card-title">
                  <h4 className="chart-title mb-0">Subscription Revenue</h4>
                  <p className="qx-card-desc mb-0">Earnings from all subscription plans</p>
                </div>
                <div className="qx-chart-card-filters" aria-hidden="true" />
                <div className="qx-chart-card-stats">
                  <div className="stat-box">
                    <div className="stat-label">Total Revenue</div>
                    <div className="stat-value">₹{totalRevenue.toLocaleString()}</div>
                  </div>
                </div>
              </div>
              <div className="qx-chart-wrap">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={subscriptionRevenueData} margin={{ top: 24, right: 24, left: 4, bottom: 12 }} barCategoryGap="28%">
                  <CartesianGrid vertical={false} stroke="#E4E7EC" strokeDasharray="4 4" />
                  <XAxis {...defaultXAxis("name")} />
                  <YAxis
                    {...defaultYAxis()}
                    tickFormatter={(value) =>
                      value >= 1000 ? `₹${(value / 1000).toFixed(0)}k` : `₹${value}`
                    }
                  />
                  <Tooltip
                    {...defaultTooltip(currencyTooltipFormatter)}
                  />
                  <Bar dataKey="revenue" name="Revenue" maxBarSize={48} radius={barRadius}>
                    {subscriptionRevenueData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              </div>

              <div className="d-flex justify-content-end mt-3">
                <button
                  className="download-btn"
                  onClick={() => {
                    const excelData = [
                      {
                        name: "Total",
                        value: subscriptionRevenueData.reduce((s, i) => s + (i.value || 0), 0),
                        revenue: subscriptionRevenueData.reduce((s, i) => s + (i.revenue || 0), 0),
                      },
                      ...subscriptionRevenueData,
                    ];
                    exportToExcel(excelData, "subscription_revenue_report");
                  }}
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg> Export CSV
                </button>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="premium-card p-4 qx-chart-card h-100">
              <div className="qx-chart-card-header">
                <div className="qx-chart-card-title">
                  <h4 className="chart-title mb-0">Multi-language Usage</h4>
                  <p className="qx-card-desc mb-0">Guest language preferences</p>
                </div>
                <div className="qx-chart-card-filters" aria-hidden="true" />
                <div className="qx-chart-card-stats">
                  <div className="stat-box">
                    <div className="stat-label">Total Switches</div>
                    <div className="stat-value">
                      {languageUsageData.reduce((s, i) => s + i.value, 0)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="qx-chart-wrap qx-lang-chart-wrap">
              {languageChartData.length ? (
                <>
                  <ResponsiveContainer width="100%" height={210}>
                    <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                      <Pie
                        data={languageChartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={58}
                        outerRadius={82}
                        paddingAngle={languageChartData.length > 1 ? 2 : 0}
                        stroke="#ffffff"
                        strokeWidth={languageChartData.length > 1 ? 2 : 0}
                        label={false}
                        labelLine={false}
                      >
                        {languageChartData.map((entry) => {
                          const colorIndex = languageLegendData.findIndex((l) => l.name === entry.name);
                          return (
                            <Cell
                              key={entry.name}
                              fill={getLanguageColor(entry, colorIndex)}
                            />
                          );
                        })}
                        <Label
                          content={({ viewBox }) => {
                            if (!viewBox || languageChartTotal === 0) return null;
                            const { cx, cy } = viewBox;
                            const topPct = topLanguage
                              ? Math.round((topLanguage.value / languageChartTotal) * 100)
                              : 0;
                            return (
                              <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">
                                <tspan
                                  x={cx}
                                  dy="-0.55em"
                                  fill="#667085"
                                  fontSize={12}
                                  fontFamily="Outfit, system-ui, sans-serif"
                                >
                                  {topLanguage?.name || "—"}
                                </tspan>
                                <tspan
                                  x={cx}
                                  dy="1.45em"
                                  fill="#1D2939"
                                  fontSize={20}
                                  fontWeight={700}
                                  fontFamily="Outfit, system-ui, sans-serif"
                                >
                                  {topPct}%
                                </tspan>
                              </text>
                            );
                          }}
                        />
                      </Pie>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const item = payload[0]?.payload;
                          if (!item) return null;
                          return (
                            <div className="qx-chart-tooltip">
                              <p className="qx-chart-tooltip-label">{item.name}</p>
                              <ul className="qx-chart-tooltip-list">
                                <li>
                                  <span
                                    className="qx-chart-tooltip-dot"
                                    style={{ background: payload[0]?.color }}
                                  />
                                  <span className="qx-chart-tooltip-name">Switches</span>
                                  <span className="qx-chart-tooltip-value">
                                    {item.value?.toLocaleString?.() ?? item.value}
                                  </span>
                                </li>
                                <li>
                                  <span className="qx-chart-tooltip-dot" style={{ background: "#E4E7EC" }} />
                                  <span className="qx-chart-tooltip-name">Share</span>
                                  <span className="qx-chart-tooltip-value">{item.pct}%</span>
                                </li>
                              </ul>
                            </div>
                          );
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="qx-lang-legend">
                    {languageLegendData.map((item, index) => (
                      <div className="qx-lang-legend-item" key={item.name}>
                        <span
                          className="qx-lang-legend-dot"
                          style={{ background: getLanguageColor(item, index) }}
                        />
                        <span className="qx-lang-legend-name">{item.name}</span>
                        <span className="qx-lang-legend-pct">{item.pct}%</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="qx-chart-empty">No language data available</div>
              )}
              </div>

            </div>
          </div>

          <div className="col-12">
            <div className="premium-card p-4 qx-chart-card">
              <div className="qx-chart-card-header">
                <div>
                  <h4 className="chart-title mb-0">Most Popular Templates</h4>
                  <p className="qx-card-desc mb-0">Frequently used invite designs</p>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Thumbnail</th>
                      <th>Template Name</th>
                      <th>Shares</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templateUsageData?.length ? (
                      templateUsageData.map((item, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            <img
                              src={`https://managemyshaadib.s3.ap-south-1.amazonaws.com/${item.thumbnail}`}
                              alt={item.name}
                              style={{ width: "60px", height: "40px", objectFit: "cover", borderRadius: "6px" }}
                              onError={(e) => { e.target.src = "https://via.placeholder.com/60x40"; }}
                            />
                          </td>
                          <td className="fw-semibold">{item.name}</td>
                          <td className="fw-bold" style={{ color: "#F59E0B" }}>{item.shareCount || 0}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-3">
                          No template data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
      </div>
      </div>

    </>
  );
};

export default Home;






